import { getDateString } from './dictionaries'

export interface GameResult {
  date: string
  word?: string
  attempts: string[]
  won: boolean
  attemptsCount: number
  completed: boolean
  gameType?: string
  time?: number
  level?: number
  hintsUsed?: number
  attemptsByLevel?: { [level: number]: number }
  totalAttempts?: number
}

export interface DailyStats {
  date: string
  word?: string
  gamesPlayed: number
  gamesWon: number
  averageAttempts: number
  bestAttempts: number
  completed: boolean
}

export interface UserStats {
  totalGamesPlayed: number
  totalGamesWon: number
  currentStreak: number
  maxStreak: number
  averageAttempts: number
  bestAttempts: number
  lastPlayedDate: string | null
}

export interface GameData {
  stats: UserStats
  history: { [date: string]: GameResult }
}

// Función para obtener la clave de localStorage para un juego
function getGameKey(gameId: string): string {
  return `game-${gameId}`
}

// Función para obtener o crear los datos del juego
function getGameData(gameId: string): GameData {
  const gameKey = getGameKey(gameId)
  const saved = localStorage.getItem(gameKey)

  if (saved) {
    try {
      return JSON.parse(saved)
    } catch {
      // Si hay error al parsear, crear datos nuevos
    }
  }

  // Crear estructura inicial
  return {
    stats: {
      totalGamesPlayed: 0,
      totalGamesWon: 0,
      currentStreak: 0,
      maxStreak: 0,
      averageAttempts: 0,
      bestAttempts: 0,
      lastPlayedDate: null
    },
    history: {}
  }
}

// Función para guardar los datos del juego
function saveGameData(gameId: string, gameData: GameData): void {
  const gameKey = getGameKey(gameId)
  localStorage.setItem(gameKey, JSON.stringify(gameData))
}

// Función para guardar el resultado de un juego
export function saveGameResult(
  gameId: string,
  attempts: string[],
  won: boolean,
  date: string = getDateString(),
  additionalData?: {
    time?: number;
    level?: number;
    hintsUsed?: number;
    attemptsByLevel?: { [level: number]: number };
    totalAttempts?: number;
  }
): void {
  const result: GameResult = {
    date,
    attempts,
    won,
    attemptsCount: attempts.length,
    completed: true,
    gameType: gameId,
    ...additionalData
  }

  // Obtener datos actuales del juego
  const gameData = getGameData(gameId)

  // Agregar resultado al historial
  gameData.history[date] = result

  // Actualizar estadísticas
  updateUserStats(gameData, result)

  // Guardar datos actualizados
  saveGameData(gameId, gameData)
}

// Función para obtener el resultado de un día específico
export function getGameResult(gameId: string, date: string): GameResult | null {
  const gameData = getGameData(gameId)
  return gameData.history[date] || null
}

// Función para verificar si ya se jugó un día específico
export function hasPlayedToday(gameId: string, date: string = getDateString()): boolean {
  return getGameResult(gameId, date) !== null
}

// Función para actualizar estadísticas generales del usuario
function updateUserStats(gameData: GameData, result: GameResult): void {
  const currentStats = gameData.stats

  // Calcular bestAttempts correctamente
  let newBestAttempts = currentStats.bestAttempts
  if (result.won) {
    if (currentStats.totalGamesPlayed === 0 || currentStats.bestAttempts === 0) {
      // Primer juego ganado o no hay best attempts válido previo
      newBestAttempts = result.attemptsCount
    } else {
      // Solo actualizar si es mejor que el anterior
      newBestAttempts = Math.min(currentStats.bestAttempts, result.attemptsCount)
    }
  }

  // Calcular currentStreak
  let newCurrentStreak = currentStats.currentStreak
  if (result.won) {
    newCurrentStreak = currentStats.currentStreak + 1
  } else {
    newCurrentStreak = 0
  }

  // Calcular maxStreak correctamente
  let newMaxStreak = currentStats.maxStreak
  if (result.won) {
    newMaxStreak = Math.max(currentStats.maxStreak, newCurrentStreak)
  }

  const newStats: UserStats = {
    totalGamesPlayed: currentStats.totalGamesPlayed + 1,
    totalGamesWon: currentStats.totalGamesWon + (result.won ? 1 : 0),
    currentStreak: newCurrentStreak,
    maxStreak: newMaxStreak,
    averageAttempts: calculateNewAverage(currentStats, result.attemptsCount),
    bestAttempts: newBestAttempts,
    lastPlayedDate: result.date
  }

  gameData.stats = newStats
}

// Función para calcular el nuevo promedio de intentos
function calculateNewAverage(currentStats: UserStats, newAttempts: number): number {
  const totalAttempts = (currentStats.averageAttempts * currentStats.totalGamesPlayed) + newAttempts
  const totalGames = currentStats.totalGamesPlayed + 1
  return Math.round((totalAttempts / totalGames) * 10) / 10
}

// Función para obtener estadísticas del usuario
export function getUserStats(gameId: string): UserStats {
  const gameData = getGameData(gameId)
  return gameData.stats
}

// Función para obtener estadísticas recientes
export function getRecentStats(gameId: string, days: number = 7): DailyStats[] {
  const stats: DailyStats[] = []
  const today = new Date()

  for (let i = 0; i < days; i++) {
    const date = new Date(today)
    date.setDate(date.getDate() - i)
    const dateString = date.toISOString().split('T')[0]
    const result = getGameResult(gameId, dateString)

    if (result) {
      stats.push({
        date: dateString,
        word: result.word,
        gamesPlayed: 1,
        gamesWon: result.won ? 1 : 0,
        averageAttempts: result.attemptsCount,
        bestAttempts: result.won ? result.attemptsCount : 0,
        completed: result.completed
      })
    }
  }

  return stats
}

// Función para obtener historial de juegos
export function getGameHistory(gameId: string): GameResult[] {
  const gameData = getGameData(gameId)
  const history = Object.values(gameData.history)
  return history.sort((a, b) => b.date.localeCompare(a.date))
}

// Función para limpiar estadísticas
export function clearStats(gameId: string): void {
  const gameKey = getGameKey(gameId)
  localStorage.removeItem(gameKey)
}

// Función para exportar estadísticas
export function exportStats(gameId: string): string {
  const gameData = getGameData(gameId)
  return JSON.stringify(gameData)
}

// Función para importar estadísticas
export function importStats(data: string): boolean {
  try {
    const importedData = JSON.parse(data)

    if (importedData.stats && importedData.history) {
      const gameId = importedData.stats.gameType || 'unknown'
      const gameKey = getGameKey(gameId)
      localStorage.setItem(gameKey, JSON.stringify(importedData))
      return true
    }

    return false
  } catch {
    return false
  }
}

// Función para limpiar todos los datos del localStorage
export function clearOldData(): void {
  const keys = Object.keys(localStorage)

  keys.forEach(key => {
    // Eliminar claves del sistema anterior
    if (key.includes('-stats') ||
      (key.includes('-') && key.match(/\d{4}-\d{2}-\d{2}$/)) ||
      key.includes('-partial-') ||
      key.includes('-instructions-') ||
      // También limpiar los nuevos formatos de estado parcial e instrucciones
      key.includes('-partial') ||
      key.includes('-instructions') ||
      // Eliminar datos de partidas (game-{gameId})
      key.startsWith('game-') ||
      // Eliminar favoritos
      key === 'favorites' ||
      // Eliminar consentimiento de cookies
      key === 'cookie-consent') {
      localStorage.removeItem(key)
    }
  })
} 